/**
 * Accessors over the CRM slice of the integration catalog.
 *
 * There is NO second list here. `INTEGRATIONS` in `../integrations.ts` is the
 * one authored place a vendor exists; this module indexes its `group: 'crm'`
 * entries and answers the questions the CRM layer asks — "does this vendor have
 * a token manager", "what does its entity URL look like". Adding a vendor is
 * still one object literal in the catalog.
 *
 * Why it exists at all rather than every consumer filtering `INTEGRATIONS`:
 * `crm` is optional on the catalog entry (telephony and messaging have none),
 * so every caller would repeat the same narrowing and the same "what if it's
 * absent" decision. That decision belongs in one place.
 *
 * Nothing here imports an implementation — the registry states FACTS, and the
 * factories in @aica/connectors read them. That is what keeps @aica/core out of
 * an import cycle with the package it describes.
 */

import {
  INTEGRATIONS,
  type CrmDeliverySupport,
  type EmbeddedCrmVendorId,
  type CrmEntityUrlType,
  type CrmIntegrationFacts,
  type CrmVendorId,
  type DealTaskCrmVendorId,
  type ProcessingInstructionCrmVendorId,
} from '../integrations';
import { neutralizeBbcode } from '../utils/bbcode';

export type {
  CrmDeliverySupport,
  CrmEntityUrlType,
  CrmIntegrationFacts,
  CrmVendorId,
  DealTaskCrmVendorId,
  EmbeddedCrmVendorId,
  ProcessingInstructionCrmVendorId,
};

export interface CrmVendorEntry extends CrmIntegrationFacts {
  readonly id: CrmVendorId;
  /** Brand name, never translated. */
  readonly displayName: string;
}

/**
 * `Record<CrmVendorId, …>` on purpose: a catalog CRM entry that forgets its
 * `crm` facts fails to build this map, so "added a vendor, forgot the CRM
 * block" is a startup error rather than an `undefined` at the first call.
 */
export const CRM_VENDORS: Record<CrmVendorId, CrmVendorEntry> = Object.fromEntries(
  INTEGRATIONS.filter(
    (entry): entry is Extract<(typeof INTEGRATIONS)[number], { group: 'crm' }> =>
      entry.group === 'crm'
  ).map((entry) => {
    if (!entry.crm) {
      throw new Error(
        `Integration catalog: CRM "${entry.id}" is missing its \`crm\` facts block`
      );
    }
    return [entry.id, { id: entry.id, displayName: entry.name, ...entry.crm }];
  })
) as unknown as Record<CrmVendorId, CrmVendorEntry>;

/** Every known CRM vendor. */
export function allCrmVendors(): CrmVendorId[] {
  return Object.keys(CRM_VENDORS) as CrmVendorId[];
}

/**
 * Is this string a CRM vendor we know about?
 *
 * "Know about", NOT "have implemented" — the distinction the old
 * `isProviderSupported` blurred, which is how it ended up returning false for a
 * vendor that was already shipping. Use the capability helpers below to ask
 * whether something actually works.
 */
export function isKnownCrmVendor(value: string): value is CrmVendorId {
  return Object.prototype.hasOwnProperty.call(CRM_VENDORS, value);
}

export function getCrmVendor(value: string): CrmVendorEntry | undefined {
  return isKnownCrmVendor(value) ? CRM_VENDORS[value] : undefined;
}

/** Vendors whose OAuth/token layer is implemented. */
export function crmVendorsWithTokenManager(): CrmVendorId[] {
  return allCrmVendors().filter((id) => CRM_VENDORS[id].tokenManager);
}

/** Vendors whose field service is implemented (field mapping, org structure). */
export function crmVendorsWithFieldService(): CrmVendorId[] {
  return allCrmVendors().filter((id) => CRM_VENDORS[id].fieldService);
}

/** Vendors we ship an in-CRM app for (placement / iframe / micro-frontend). */
export function crmVendorsWithEmbeddedApp(): EmbeddedCrmVendorId[] {
  return allCrmVendors().filter(
    (id): id is EmbeddedCrmVendorId => CRM_VENDORS[id].embeddedApp
  );
}

/** Vendors whose installs need an active processing instruction (see the catalog fact). */
export function crmVendorsRequiringProcessingInstruction(): ProcessingInstructionCrmVendorId[] {
  return allCrmVendors().filter(
    (id): id is ProcessingInstructionCrmVendorId => CRM_VENDORS[id].processingInstruction
  );
}

/**
 * Does an install of this vendor need an active processing instruction before
 * anything reads or writes it (#2136)? An unknown vendor — or no vendor — is
 * `false`: nothing is known about an instruction to wait for, and the other
 * vendor-neutral gates (delivery support) answer unknown the same way.
 */
export function requiresProcessingInstruction(
  vendor: string | null | undefined
): vendor is ProcessingInstructionCrmVendorId {
  return !!vendor && getCrmVendor(vendor)?.processingInstruction === true;
}

/**
 * Were this vendor's deal stage and funnel reads checked on a live portal (see
 * the catalog fact)? An unknown vendor is `false`: a read nobody has verified
 * takes the soft path — skip or the standard methodology — rather than a verdict.
 */
export function crmStageReadVerified(vendor: string | null | undefined): boolean {
  return !!vendor && getCrmVendor(vendor)?.stageReadVerified === true;
}

/**
 * Does this vendor's stage id name its own funnel? An unknown vendor is `false`:
 * nothing is known about its id format, so the funnel's stage list decides.
 */
export function crmStageIdEncodesPipeline(vendor: string | null | undefined): boolean {
  return !!vendor && getCrmVendor(vendor)?.stageIdEncodesPipeline === true;
}

/**
 * Text written into this vendor's CRM: model text and quotes go through here, so
 * a CRM that renders BB-code gets tags that cannot become markup. Only a CRM
 * the catalog names as plain text gets it as is: an unknown vendor gets the
 * safe answer — neutralized — should it ever receive a comment or a task.
 */
export function toCrmText(vendor: string, text: string): string {
  return getCrmVendor(vendor)?.textMarkup === 'plain' ? text : neutralizeBbcode(text);
}

/** Can a chat built from this vendor's CRM activity be read back by the activity id? */
export function crmChatActivityRefetch(vendor: string | null | undefined): boolean {
  return !!vendor && getCrmVendor(vendor)?.chatActivityRefetch === true;
}

/**
 * Does this vendor's embedded session travel as `Authorization: Bearer` rather
 * than the cookie (see the catalog fact)? An unknown vendor is `false`: the
 * cookie is the credential every other embedded session uses.
 */
export function embeddedSessionUsesBearer(vendor: string | null | undefined): boolean {
  return !!vendor && getCrmVendor(vendor)?.embeddedSessionCredential === 'bearer';
}

/**
 * Must the user claiming ownership of the organization an in-CRM app onboards
 * be the installer the CRM named (see the catalog fact)? Only a vendor the
 * catalog names `first_visitor` answers `false`; an unknown vendor and one
 * without an in-CRM app answer `true` — no claim without an explicit opt-in.
 */
export function onboardingOwnerNeedsInstallerMatch(
  vendor: string | null | undefined
): boolean {
  return !vendor || getCrmVendor(vendor)?.onboardingOwnerClaim !== 'first_visitor';
}

/**
 * Can a deal task be written into this vendor's CRM (`delivery.dealTask`)? An
 * unknown vendor is `false`, like every other delivery fact.
 */
export function supportsDealTask(
  vendor: string | null | undefined
): vendor is DealTaskCrmVendorId {
  return !!vendor && getCrmDeliverySupport(vendor).dealTask;
}

/**
 * The same list shaped for `z.enum`, which needs a non-empty tuple.
 *
 * Six schemas and unions used to spell `['bitrix24', 'uspacy']` by hand — two in
 * one file — and each was a place a third embedded CRM would be rejected at the
 * door with a validation error naming a field, not a vendor.
 */
export function embeddedCrmVendorEnum(): [EmbeddedCrmVendorId, ...EmbeddedCrmVendorId[]] {
  // The TYPE is the embedded subset, not `CrmVendorId`: a schema built on the
  // wider one would accept `'hubspot'` at compile time while refusing it at
  // runtime, which is worse than the hand-written pair it replaces.
  return crmVendorsWithEmbeddedApp() as [EmbeddedCrmVendorId, ...EmbeddedCrmVendorId[]];
}

/**
 * What this vendor's delivery path supports.
 *
 * An unknown vendor gets the all-false answer, which is the safe direction: the
 * deliver worker skips rather than assuming Bitrix24 behaviour. That default is
 * exactly what the original scattered `providerName === 'uspacy'` checks got
 * wrong — every place one was missing, a new vendor inherited a capability it
 * does not have.
 */
export function getCrmDeliverySupport(vendor: string): CrmDeliverySupport {
  return getCrmVendor(vendor)?.delivery ?? UNKNOWN_VENDOR_DELIVERY;
}

const UNKNOWN_VENDOR_DELIVERY: CrmDeliverySupport = {
  structuredFields: false,
  smartProcess: false,
  deliveryReceipts: false,
  localEntityIdsOnly: false,
  activityOnlyDelivery: false,
  // Unknown vendor writes no note at all; `false` keeps an approval publishing
  // rather than assuming a replace that nobody promised (#742).
  timelineNoteReplaces: false,
  dealStateComment: false,
  dealTask: false,
};

/**
 * Link to an entity card, or null when this vendor has no known URL shape or no
 * portal domain is available.
 */
export function buildCrmEntityUrl(input: {
  vendor: string;
  domain: string | null | undefined;
  entityType: CrmEntityUrlType;
  entityId: string;
}): string | null {
  const entry = getCrmVendor(input.vendor);
  if (!entry?.buildEntityUrl) return null;
  const domain = input.domain?.replace(/^https?:\/\//, '').trim();
  if (!domain) return null;
  return entry.buildEntityUrl({
    domain,
    entityType: input.entityType,
    entityId: input.entityId,
  });
}
